import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';

import { Nbsp } from '@/components/nbsp';
import { Button, Badge, cn } from '@simplywallst/ui-core/next';

import { ROUTE_PORTFOLIO } from '@/constants/routes';

import {
  usePortfolioTransactions,
  useUpdateTransactionPortfolio,
} from '../hooks';

import type { TransactionInput } from '../hooks';

import { Table, useTableValidation } from '../components/Table';
import { Error, RowsError } from '../components/Error';
import { Layout } from '../components/Layout';

import type { Validation } from '../components/Table';

import { isTransaction } from '../utils';

import { getTableDataFromTransactions } from './utils';

interface Props {
  portfolioId: string;
}

export const PopulatedState = ({ portfolioId }: Props) => {
  const dispatch = useDispatch();

  const { validateRows, highlightRegions } = useTableValidation();

  const [scrollToIndex, setScrollToIndex] = useState<number | undefined>();
  const [showError, setShowError] = useState(false);
  const [validation, setValidation] = useState<Validation | null>(null);

  const { data, status } = usePortfolioTransactions({ id: portfolioId });

  const { mutate, status: mutationStatus } = useUpdateTransactionPortfolio({
    onSuccess: () => {
      dispatch({
        type: ROUTE_PORTFOLIO,
        payload: { portfolioId },
      });
    },
  });

  const handleSave = useCallback(() => {
    if (validation === null) return;

    const { error } = validation;

    if (error !== null) {
      setShowError(true);
      return;
    }

    if (validation.populatedRows.length === 0) return;

    const rows = validation.populatedRows.map(([, row]) => row);
    const transactions: TransactionInput[] = [];

    for (const row of rows) {
      const transaction = {
        symbol: row.ticker.symbol,
        exchangeCode: row.ticker.exchangeCode,
        date: row.date?.toISOString().split('T')[0],
        units: row.shares,
        value: row.price,
        totalValue: row.cost,
        type: row.type?.toUpperCase(),
      };

      if (isTransaction(transaction)) {
        transactions.push(transaction);
      }
    }

    if (transactions.length === 0) return;

    mutate({ id: portfolioId, transactions });
  }, [mutate, validation, portfolioId]);

  const handleUpdate = useMemo(
    () => debounce((rows) => setValidation(validateRows(rows))),
    [validateRows]
  );

  const tableData = useMemo(
    () => getTableDataFromTransactions(data?.Portfolio?.transactions || []),
    [data]
  );

  if (status === 'pending') return;

  const populatedRowCount = validation?.populatedRows.length ?? 0;
  const incompleteRowCount = validation?.invalidRows.length ?? 0;
  const ariaStatus = `${
    populatedRowCount - incompleteRowCount
  }/${populatedRowCount} transactions ready`;

  return (
    <Layout>
      <div className="grid grid-rows-[1fr_max-content]">
        <Table
          data={tableData}
          scrollToIndex={scrollToIndex}
          onUpdate={handleUpdate}
          highlightRegions={showError ? highlightRegions : undefined}
        />
        <div
          className={cn(
            'grid',
            showError ? 'grid-rows-[56px_72px]' : 'grid-rows-[72px]'
          )}
        >
          {showError && validation && (
            <Error
              onDismiss={() => {
                setShowError(false);
              }}
            >
              {validation.errorRowRanges ? (
                <RowsError
                  message={validation.error ?? ''}
                  ranges={validation.errorRowRanges}
                  onRangeClick={setScrollToIndex}
                />
              ) : (
                <>{validation.error}</>
              )}
            </Error>
          )}
          <div className="grid grid-cols-[minmax(auto,_1200px)] justify-center items-center bg-surface-1">
            <div className="grid grid-cols-[max-content_max-content] justify-between items-center px-x2">
              <Button variant="outline">
                Discard
                <span className="hidden sm:inline-block"> and go back</span>
              </Button>
              <div className="grid grid-cols-[max-content_max-content] justify-end items-center gap-x2">
                <Badge className="text-xs text-solid px-x1_75">
                  <span role="status" aria-label={ariaStatus}>
                    <strong>
                      {`${
                        populatedRowCount - incompleteRowCount
                      } / ${populatedRowCount}`}
                    </strong>
                    <Nbsp />
                    <Nbsp />
                    <span className="text-soft">
                      <span className="hidden sm:inline-block">
                        transactions{' '}
                      </span>
                      ready
                    </span>
                  </span>
                </Badge>
                <Button
                  variant="primary"
                  className="min-w-[100px]"
                  isDisabled={showError}
                  isLoading={mutationStatus === 'pending'}
                  onClick={handleSave}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
